import { useEnvSettings, useMarketLocation, useTokenInfo } from '@e2m-customer-portal/hooks';
import { convertMeasure, MachineTechnology, parseNumber, UserRole } from '@e2m-customer-portal/shared';
import { Container, ErrorComponent, MasterDataItem, PageTitle } from '@e2m-customer-portal/ui';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { mostCompactName } from 'utils/most-compact-name';

interface MasterDataMarketLocationProps {
  marketLocationId: string;
}

export function MasterDataMarketLocation({
  marketLocationId,
}: Readonly<MasterDataMarketLocationProps>): ReactElement<MasterDataMarketLocationProps> {
  const [t] = useTranslation();
  const { roles } = useTokenInfo();
  const [marketLocation, error] = useMarketLocation(marketLocationId);

  if (!marketLocation || error) {
    return <ErrorComponent error={error} />;
  }
  const sortedReleases = [...marketLocation.products].sort((a, b) => parseNumber(a.id) - parseNumber(b.id));
  const { marketingPortalURL } = useEnvSettings();
  const marketLocationName = marketLocation.linkedMarketLocation
    ? `${marketLocation.name} | ${marketLocation.linkedMarketLocation.name}`
    : marketLocation.name;

  return (
    <>
      <PageTitle title={t('MASTER_DATA_HEADER')} />

      <Container id={marketLocation.id} title={marketLocationName} maxWidth="md">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
          <MasterDataItem
            header={t('MASTER_DATA_GRID_ALIAS_NAME')}
            body={marketLocation.name}
            mainType={marketLocation.type}
            linkedMarketLocationBody={marketLocation.linkedMarketLocation?.name}
            linkedType={marketLocation.linkedMarketLocation?.type}
          />
          <MasterDataItem header={t('MASTER_DATA_GRID_MARKET_LOCATION_ID')}>
            <>
              <div className="break-all font-semibold">
                {marketLocation.linkedMarketLocation && (
                  <p className="font-semibold text-blue-grey-200 text-xs m-0 p-0">
                    {t(`SHARED_${marketLocation.type}`)}:
                  </p>
                )}
                {marketLocation.marketLocationId}
              </div>
              {marketLocation.linkedMarketLocation && (
                <div className="break-all font-semibold">
                  <p className="font-semibold text-blue-grey-200 text-xs m-0 p-0">
                    {t(`SHARED_${marketLocation.linkedMarketLocation.type}`)}:
                  </p>
                  {marketLocation.linkedMarketLocation.marketLocationId}
                </div>
              )}
            </>
          </MasterDataItem>
          {roles.has(UserRole.VPPOperator) && (
            <MasterDataItem header={t('MASTER_DATA_GRID_MARKET_LOCATION_SERIAL_NUMBER')}>
              {marketLocation.linkedMarketLocation && (
                <p className="font-semibold text-blue-grey-200 text-xs m-0 p-0">
                  {t(`SHARED_${marketLocation.type}`)}:
                </p>
              )}
              <Link
                target="_blank"
                to={`${marketingPortalURL}marketing/market-locations/${marketLocation.number}/general-and-machines`}
              >
                {marketLocation.number}
              </Link>
              {marketLocation.linkedMarketLocation && (
                <div>
                  <p className="font-semibold text-blue-grey-200 text-xs m-0 p-0">
                    {t(`SHARED_${marketLocation.linkedMarketLocation.type}`)}:
                  </p>
                  <Link
                    target="_blank"
                    to={`${marketingPortalURL}marketing/market-locations/${marketLocation.linkedMarketLocation.number}/general-and-machines`}
                  >
                    {marketLocation.linkedMarketLocation.number}
                  </Link>
                </div>
              )}
            </MasterDataItem>
          )}
          <MasterDataItem
            header={t('MASTER_DATA_GRID_TECHNOLOGY')}
            body={mostCompactName(
              ...marketLocation.technologies
                .filter((technology) => technology !== MachineTechnology.COMPOSITE)
                .map((technology) => t(`SHARED_TECHNOLOGY_${technology}`)),
            )}
          />
          <MasterDataItem
            header={t('MASTER_DATA_GRID_NUMBER_OF_MACHINES')}
            body={marketLocation.machines.length}
            mainType={marketLocation.type}
            linkedMarketLocationBody={marketLocation.linkedMarketLocation?.machines.length}
            linkedType={marketLocation.linkedMarketLocation?.type}
          />
          <MasterDataItem
            header={t('MASTER_DATA_GRID_MACHINES_TOTAL_OUTPUT')}
            body={convertMeasure(marketLocation.machinesTotalOutput, 'MW')}
            mainType={marketLocation.type}
            linkedMarketLocationBody={
              marketLocation.linkedMarketLocation &&
              convertMeasure(marketLocation.linkedMarketLocation.machinesTotalOutput, 'MW')
            }
            linkedType={marketLocation.linkedMarketLocation?.type}
          />
          <MasterDataItem header={t('MASTER_DATA_GRID_CONTRACT_PARTNER')} body={marketLocation.contractPartner} />
          <MasterDataItem
            header={t('MASTER_DATA_GRID_DISTRIBUTION_NETWORK_OPERATOR')}
            body={marketLocation.distributionNetworkOperator}
          />
          <MasterDataItem
            header={t('MASTER_DATA_GRID_TRANSMISSION_SYSTEM_OPERATOR')}
            body={marketLocation.transmissionSystemOperator}
          />
          <MasterDataItem header={t('MASTER_DATA_GRID_MARKETING_START')} body={marketLocation.marketingStart} />
          <MasterDataItem
            header={t('MASTER_DATA_GRID_RELEASES')}
            body={sortedReleases ? sortedReleases.map((product) => product.name).join(', ') : ''}
          />
        </div>
      </Container>
    </>
  );
}
